import { Component } from '@theme/component';

/**
 * AnnouncementCountdownBanner
 *
 * Counts down to a single fixed, merchant-configured end date/time
 * (section.settings.end_datetime, an ISO 8601 string with a UTC offset -
 * see the section schema). There is no relative duration and no client-
 * side persistence of any kind (no localStorage/sessionStorage/cookies):
 * the same absolute timestamp is parsed fresh on every page load, for
 * every visitor and every device, so refreshing, navigating away and
 * back, opening a new tab, or opening a private/incognito window never
 * changes what's being counted down to - it's always
 * end_datetime - Date.now().
 *
 * Config is passed in from the section via a data attribute (Shopify's
 * equivalent of a co-located config object, editable in the theme editor
 * without touching this file):
 *   data-end-datetime -> ISO 8601 end timestamp, e.g. "2026-08-08T23:59:59+01:00"
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementCountdownBanner extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {number | undefined} */
  #intervalId;

  /** @type {number} */
  #endTime = 0;

  connectedCallback() {
    super.connectedCallback();

    // Browser-only: window is never touched during Liquid's server render,
    // only once this custom element hydrates client-side.
    if (typeof window === 'undefined') return;

    const parsed = Date.parse(this.dataset.endDatetime ?? '');
    this.#endTime = Number.isFinite(parsed) ? parsed : 0;

    this.update();

    // Nothing left to tick towards if the end date is missing/invalid or
    // already in the past - avoid starting an interval that has nothing
    // left to do.
    if (!this.#endTime || this.#endTime <= Date.now()) return;

    this.#intervalId = window.setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearInterval();
  }

  #clearInterval() {
    if (this.#intervalId !== undefined) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }
  }

  update() {
    const remaining = Math.max(0, this.#endTime - Date.now());

    // Reached the fixed end date - stop ticking rather than looping into
    // a new cycle. The display simply holds at zero.
    if (remaining === 0) {
      this.#clearInterval();
    }

    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remaining / (60 * 60 * 1000)) % 24);
    const minutes = Math.floor((remaining / (60 * 1000)) % 60);
    const seconds = Math.floor((remaining / 1000) % 60);

    const { days: daysEl, hours: hoursEl, minutes: minutesEl, seconds: secondsEl } = this.refs;

    daysEl.textContent = String(days).padStart(2, '0');
    hoursEl.textContent = String(hours).padStart(2, '0');
    minutesEl.textContent = String(minutes).padStart(2, '0');
    secondsEl.textContent = String(seconds).padStart(2, '0');
  }
}

if (!customElements.get('announcement-countdown-banner')) {
  customElements.define('announcement-countdown-banner', AnnouncementCountdownBanner);
}
